import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackLead } from "@/lib/pixel";

interface PrayerFormProps {
  trackingId?: string;
  locationId?: string;
  projectId?: string;
}

const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<string, unknown>;
    formLabels: Record<string, string>;
  },
  options: {
    customFields?: Record<string, { value?: unknown; label: string }>;
  } = {},
) => {
  const { customFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };
  const body = new FormData();

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined) continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  body.append("event", JSON.stringify(eventPayload));

  fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: {
      version: "2021-07-28",
    },
    body,
  }).catch(() => {});
};

const PrayerForm: React.FC<PrayerFormProps> = ({
  trackingId = "tk_b8bba26f2eb5420481cadf156a166208",
  locationId = "N5B75kjUT85NEZBh2RaZ",
  projectId = "1787853701390843161",
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [prayerRequest, setPrayerRequest] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !prayerRequest) {
      toast({
        title: "Please complete all fields",
        description:
          "We want to know who we are praying for and how to reach you.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "prayer-request-form",
      formData: {
        name,
        email,
      },
      formLabels: {
        name: "Name",
        email: "Email Address",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: trackingId,
      locationId: locationId,
      projectId: projectId,
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
        source: "ai_studio",
        projectId: projectId,
        formName: "Prayer Request Form",
      },
    };

    postTrackingEvent(trackingPayload, {
      customFields: {
        QkEdItJPfLvLv5VuIjgt: {
          value: prayerRequest,
          label: "Prayer Request",
        },
      },
    });

    trackLead({
      content_name: "Prayer Request Form",
      content_category: "Prayer",
      status: "submitted",
    });

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      toast({
        title: "Prayer Request Received",
        description: "Thank you for sharing. We stand with you in prayer.",
      });
    }, 600);
  };

  return (
    <section className="bg-white py-16 md:py-24 text-[#380C1E] border-t border-[#E8DEC8]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs uppercase font-medium tracking-widest text-[#9E1B50] italic mb-3">
          Before you go
        </p>
        <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-[#380C1E] mb-4">
          How can I pray for you?
        </h2>
        <p className="text-base md:text-lg text-[#380C1E]/80 max-w-xl mx-auto mb-8 font-light">
          You do not have to carry every part of this season alone.
        </p>

        {submitted ? (
          <div className="bg-[#4A0E26] text-white p-8 rounded-3xl max-w-xl mx-auto shadow-sm">
            <h3 className="font-serif text-2xl font-normal text-white mb-2">
              Your prayer request has been sent.
            </h3>
            <p className="text-white/80 text-sm">
              Ieshia and our prayer team hold your request close to heart. May
              God meet you right where you are.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setName("");
                setEmail("");
                setPrayerRequest("");
              }}
              className="mt-6 text-xs text-white/80 underline uppercase tracking-wider hover:text-white"
            >
              Send another prayer request
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="max-w-2xl mx-auto flex flex-col gap-3 items-stretch"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                className="w-full bg-white text-[#380C1E] border border-[#E8DEC8] rounded-full px-6 py-4 text-sm focus:outline-none focus:border-[#4A0E26] shadow-sm"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full bg-white text-[#380C1E] border border-[#E8DEC8] rounded-full px-6 py-4 text-sm focus:outline-none focus:border-[#4A0E26] shadow-sm"
              />
            </div>
            <textarea
              value={prayerRequest}
              onChange={(e) => setPrayerRequest(e.target.value)}
              placeholder="Share your prayer request"
              required
              rows={4}
              className="w-full bg-white text-[#380C1E] border border-[#E8DEC8] rounded-3xl px-6 py-4 text-sm focus:outline-none focus:border-[#4A0E26] shadow-sm resize-none"
            />
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="shrink-0 bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all shadow-sm disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send prayer request →"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default PrayerForm;
